import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {EmpionButton} from '../../../components/EmpionButton/EmpionButton';
import {useTrackingEvents} from '../../../hooks/useTrackingEvents';
import {orangeColors, neutral} from '../../../layouts/theme/designSystem';
import {CulturePillar} from '../../../types/B2B/B2BTypes';
import {TrackingEvents} from '../../../types/TrackingEvents';
import {safelyUseLanguageProfile} from '../../../utils/safelyUseLanguageProfile';

interface CultureDimensionsSectionProps {
  culturePillars: CulturePillar[];
}

export const CultureDimensionsSection = ({culturePillars}: CultureDimensionsSectionProps) => {
  const {t, i18n} = useTranslation();
  const currentLanguage = i18n.resolvedLanguage as 'en' | 'de';
  const {eventAttributes} = useTrackingEvents({
    version: TrackingEvents.Version.v1,
    category: TrackingEvents.Category.Marketing,
    action: TrackingEvents.Action.B2BBookAppointmentClicked,
    payload: {
      page: 'b2b_results',
      section: 'culture_dimensions',
    },
  });
  const visiblePillars = culturePillars.slice(0, 4);

  return (
    <Grid
      width='100%'
      sx={{
        py: {xs: 4, md: 6},
        px: {xs: 2, md: 4},
        backgroundColor: '#fff',
      }}
    >
      <Typography
        sx={{
          color: neutral['primary-black'],
          fontWeight: 700,
          fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'},
          lineHeight: 1.15,
          textAlign: 'center',
        }}
      >
        {t('b2b.results.dimensions.headline')}
      </Typography>

      <Box
        sx={{
          mt: {xs: 3, md: 4},
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'},
          gap: 2,
        }}
      >
        {visiblePillars.map((pillar, index) => {
          const isBlurredCard = index >= 2;

          return (
            <Box
              key={pillar.resourceId}
              sx={{
                minHeight: '188px',
                borderRadius: '13px',
                border: '2px solid #ffbf3e',
                backgroundColor: '#fff5e8',
                px: 2,
                py: 1.75,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  filter: isBlurredCard ? 'blur(4px)' : 'none',
                  opacity: isBlurredCard ? 0.75 : 1,
                  transition: 'filter 0.2s ease, opacity 0.2s ease',
                }}
              >
                <Typography
                  sx={{
                    color: '#163438',
                    fontWeight: 700,
                    fontSize: {xs: '1.125rem', md: '1.5rem'},
                    lineHeight: 1.4,
                  }}
                >
                  {safelyUseLanguageProfile(currentLanguage, pillar.pillarTitle)}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    color: '#163438',
                    fontWeight: 400,
                    fontSize: {xs: '0.875rem', md: '0.95rem'},
                    lineHeight: 1.5,
                  }}
                >
                  {safelyUseLanguageProfile(currentLanguage, pillar.pillarDescription)}
                </Typography>
              </Box>
              {isBlurredCard && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background: 'linear-gradient(180deg, rgba(255,245,232,0.16) 0%, rgba(255,245,232,0.5) 100%)',
                    backdropFilter: 'blur(1.5px)',
                    WebkitBackdropFilter: 'blur(1.5px)',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <EmpionButton
        onClick={() => document.getElementById('hubspot-widget')?.scrollIntoView({behavior: 'smooth'})}
        {...eventAttributes}
        sx={{
          mt: {xs: 3, md: 4},
          px: 3,
          py: 1.25,
          width: {xs: '100%', sm: 'auto'},
          minHeight: '70px',
          maxWidth: '640px',
          display: 'flex',
          mx: 'auto',
          backgroundColor: orangeColors[900],
          '&:hover': {
            backgroundColor: orangeColors[950],
          },
        }}
      >
        {t('b2b.hero.bookingButton')}
      </EmpionButton>
    </Grid>
  );
};
